import KML from "ol/format/KML";
import WKT from 'ol/format/WKT';
import { Fill, Style as OLStyle, Stroke, Text } from 'ol/style';
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import React from "react";
import { withTranslation } from "react-i18next";
import { TreeService } from "./TreeService";

class LayerControlFileExport extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedFormat: null,
            formats: [
                { label: "KML", value: "kml" },
                { label: "JPEG", value: "jpeg" },
            ],
        };
        this.toastRef = React.createRef();
    }

    exportFile = () => {
        const { selectedFormat } = this.state;
        const { mapTool, featureIds, t } = this.props;
        const map = mapTool.getMap();

        if (!selectedFormat || !map) {
            this.toastRef.current?.show({
                severity: "warn",
                summary: t('layerControlFileExport.toast_warning'),
                detail: t('layerControlFileExport.toast_select_format'),
            });
            return;
        }

        if (featureIds.length == 0) {
            this.toastRef.current?.show({
                severity: "warn",
                summary: t('layerControlFileExport.toast_warning'),
                detail: t('layerControlFileExport.toast_no_feature'),
            });
            return;
        }

        TreeService.GetLayersFromQueryToExport(featureIds).then(response => {
            if (!response.success || response.data.length === 0) {
                this.toastRef.current?.show({
                    severity: "warn",
                    summary: t('layerControlFileExport.toast_warning'),
                    detail: t('layerControlFileExport.toast_no_feature'),
                });
                return;
            }

            const formatWKT = new WKT();
            const features = response.data
                .filter(item => item.geoloc_wkt)
                .map(item => {
                    const feature = formatWKT.readFeature(item.geoloc_wkt, {
                        dataProjection: 'EPSG:4326',
                        featureProjection: 'EPSG:3857',
                    });

                    feature.set("name", item.name || "");

                    if (feature.getGeometry().getType() === "Point") {
                        if (item.icon && item.icon !== process.env.PUBLIC_URL + "/marker.png") {
                            feature.setStyle(mapTool.setFeatureStyleIcon({ src: item.icon }));
                        } else if (item.icon === process.env.PUBLIC_URL + "/marker.png") {
                            feature.setStyle(mapTool.setFeatureStyleIcon({ src: process.env.PUBLIC_URL + "/marker.png" }));
                        } else {
                            feature.setStyle(mapTool.setFeatureStyleIcon({
                                isCircle: true,
                                strokeWidth: 1,
                                strokeColor: '#000',
                                fillColor: item["backgroundcolor"]
                                    ? this.props.hexToRGBA(item["backgroundcolor"], 0.60)
                                    : 'rgba(0, 0, 255, 0.1)'
                            }));
                        }
                    } else {
                        const style = new OLStyle({
                            fill: new Fill({
                                color: item["backgroundcolor"]
                                    ? this.props.hexToRGBA(item["backgroundcolor"], 0.60)
                                    : 'rgba(0, 0, 255, 0.1)'
                            }),
                            stroke: new Stroke({
                                color: 'blue',
                                width: 1
                            }),
                            text: new Text({
                                text: item.name || '',
                                font: '12px Calibri,sans-serif',
                                fill: new Fill({ color: '#000' }),
                                stroke: new Stroke({ color: '#fff', width: 2 })
                            })
                        });
                        feature.setStyle(style);
                    }

                    return feature;
                });

            if (selectedFormat === "kml") {
                const kmlFormat = new KML();
                const kmlData = kmlFormat.writeFeatures(features, {
                    featureProjection: "EPSG:3857",
                });

                const blob = new Blob([kmlData], { type: "application/vnd.google-earth.kml+xml" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "exported_map.kml";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.toastRef.current?.show({
                    severity: "success",
                    summary: t('layerControlFileExport.toast_export'),
                    detail: t('layerControlFileExport.toast_kml_downloaded'),
                });
            } else if (selectedFormat === "jpeg") {
                const layer = mapTool.findLayer("workspace-layer");
                const source = layer.getSource();
                const addedFeatures = features.map(f => f.clone());
                addedFeatures.forEach(f => source.addFeature(f));

                const extent = source.getExtent();

                addedFeatures.forEach(f => source.removeFeature(f));

                const renderMapToImage = () => {
                    const mapCanvas = document.createElement("canvas");
                    const size = map.getSize();
                    mapCanvas.width = size[0];
                    mapCanvas.height = size[1];
                    const mapContext = mapCanvas.getContext("2d");

                    Array.from(document.querySelectorAll(".ol-layer canvas")).forEach((canvas) => {
                        if (canvas.width > 0) {
                            const opacity = canvas.parentNode.style.opacity;
                            mapContext.globalAlpha = opacity === "" ? 1 : Number(opacity);

                            const transformStr = canvas.style.transform;
                            const matrix = (transformStr?.match(/^matrix\(([^)]*)\)$/)?.[1] || '')
                                .split(",")
                                .map(Number);

                            if (matrix.length === 6) {
                                mapContext.setTransform(...matrix);
                                mapContext.drawImage(canvas, 0, 0);
                            }
                        }
                    });

                    const dataUrl = mapCanvas.toDataURL("image/jpeg");
                    const link = document.createElement("a");
                    link.href = dataUrl;
                    link.download = "map_export.jpeg";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                };

                const handleRenderComplete = () => {
                    renderMapToImage();
                    map.un("rendercomplete", handleRenderComplete);
                };

                map.once("rendercomplete", handleRenderComplete);
                map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 0 });
                setTimeout(() => map.renderSync(), 100);
            }
        });
    };


    render() {
        const { selectedFormat, formats } = this.state;
        const { visible, onHide, t } = this.props;

        return (
            <div>
                <Toast ref={this.toastRef} />
                <Dialog
                    header={t('layerControlFileExport.dialog_header')}
                    visible={visible}
                    style={{ width: "400px" }}
                    onHide={onHide}
                    modal
                >
                    <div className="p-fluid">
                        <div className="p-field">
                            <label htmlFor="format">{t('layerControlFileExport.label_select_format')}</label>
                            <select
                                id="format"
                                className="p-inputtext p-component"
                                value={selectedFormat || ""}
                                onChange={(e) =>
                                    this.setState({ selectedFormat: e.target.value })
                                }
                            >
                                <option value="" disabled>
                                    {t('layerControlFileExport.option_select_format')}
                                </option>
                                {formats.map((format) => (
                                    <option key={format.value} value={format.value}>
                                        {format.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="p-field mt-4">
                            <Button
                                label={t('layerControlFileExport.button_export')}
                                icon="pi pi-download"
                                onClick={this.exportFile}
                            />
                        </div>
                    </div>
                </Dialog>
            </div>
        );
    }

}
export default withTranslation()(LayerControlFileExport);